<template>
  <form @submit.prevent="submit" data-theme="parmazip" class="flex flex-col gap-8">
    <div class="grid grid-cols-2 gap-4">
      <template v-for="(formField, index) in formFields" :key="index">
        <div :class="`pz-form-group col-span-full`">
          <div class="flex justify-between py-2">
            <span class="pz-label font-medium text-gray-700">
              <span v-if="showLabel">{{formField.label}} <span v-if="formField?.rules?.required" class="text-error">*</span></span>
            </span>
          </div>

          <template v-if="formField.type === 'input'">
            <div class="flex flex-col gap-4">
              <!-- Upload controls -->
              <div class="bg-gray-50 border border-gray-200 rounded-xl p-5">
                <div class="flex flex-row gap-3 items-center">
                  <button 
                    class="pz-btn-primary text-white normal-case flex items-center gap-2 shadow-sm" 
                    @click.prevent.stop="openDialog"
                  >
                    <i class="las la-upload text-xl"></i>
                    Upload Images
                  </button>
                  <!-- Remove for now -->
                  <!-- <button 
                    class="pz-btn-outlined btn-primary normal-case flex items-center gap-2" 
                    @click.prevent.stop="openDrawDialog"
                  >
                    <i class="las la-paint-brush text-xl"></i>
                    Draw
                  </button> -->
                  <div class="text-sm text-gray-500 ml-2">
                    <i class="las la-info-circle mr-1"></i>
                    Upload images or create drawings to attach to this record
                  </div>
                </div>
              </div>
              
              <!-- Image preview grid -->
              <div v-if="model[formField.field] && model[formField.field]?.length > 0" 
                class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 w-full"
              >
                <Viewer 
                  :images="model[formField.field]" 
                  @inited="inited" 
                  class="viewer flex flex-row gap-2 flex-wrap" 
                  ref="viewer"
                >
                  <template #default="scope">
                    <div 
                      v-for="(src, index) in scope.images" 
                      :key="src" 
                      class="group relative bg-white border border-gray-200 rounded-xl p-2 shadow-sm hover:shadow-md transition-all"
                    >
                      <div class="relative">
                        <img 
                          :src="src" 
                          :key="src" 
                          class="w-full h-[160px] object-contain rounded-lg hover:cursor-pointer"
                        >
                        <div class="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                            class="pz-btn-text btn-sm btn-circle btn-error text-white shadow-md"
                            @click.prevent="openConfirmDialog(model[formField.field], index)"
                          >
                            <i class="las la-times"></i>
                          </button>
                        </div>
                      </div>
                      <div class="mt-2 text-center text-xs text-gray-500">
                        Image #{{ index + 1 }}
                      </div>
                    </div>
                  </template>
                </Viewer>
              </div>
              
              <!-- Empty state -->
              <div v-else class="bg-gray-50 border border-gray-200 p-8 w-full rounded-xl flex flex-col items-center justify-center">
                <div class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-3">
                  <i class="las la-image text-gray-400 text-3xl"></i>
                </div>
                <p class="text-gray-500 text-center">No attachments available</p>
                <p class="text-gray-400 text-sm text-center mt-1">Upload images or create drawings using the buttons above</p>
              </div>
            </div>
          </template>

          <template v-if="formField.type === 'text'">
            <div class="mb-2">
              <input
                v-model="model[formField.field]"
                :placeholder="formField.placeholder || 'Input Title'"
                class="flex-auto pz-input  w-full"
                :type="formField.text"
                :disabled="loading"
              />
            </div>
          </template>

          <template v-if="formField.type === 'textarea'">
            <textarea
              v-model="model[formField.field]"
              :placeholder="formField.placeholder || 'Add a description...'"
              class="h-[100px] pz-textarea  w-full"
              :disabled="loading"
            ></textarea>
          </template>
        </div>
      </template>
    </div>
    
    <!-- Footer buttons -->
    <div class="flex gap-4 justify-end border-t border-gray-200 pt-5 mt-2">
      <button
        v-if="formSubmittable"
        type="button"
        class="pz-btn-outlined normal-case"
        @click="resetForm"
      >
        <i class="las la-times mr-1"></i>
        Clear All
      </button>
      <button
        type="submit"
        class="pz-btn-primary normal-case text-white"
        :disabled="!formSubmittable || loading"
      >
        <i class="las la-save mr-1"></i>
        {{ loading ? 'Saving...' : 'Save Attachments' }}
      </button>
    </div>
  </form>

  <!-- Delete confirmation modal -->
  <ClientOnly>
    <teleport to="body">
      <div v-if="isRevealed" class="modal modal-open">
        <div class="modal-box max-w-md">
          <h3 class="font-bold text-lg mb-2">Confirm Delete</h3>
          <p class="py-4 text-gray-700">
            Are you sure you want to delete this attachment? This action cannot be undone.
          </p>
          <div class="modal-action">
            <button class="pz-btn-outlined" @click="cancel">Cancel</button>
            <button class="pz-btn-error text-white" @click="confirm">
              <i class="las la-trash mr-1"></i>
              Delete
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </ClientOnly>
</template>

<script>
import { UPDATE_FIELDS, FORM_MODEL, FORM_FIELDS, FORM_RULES } from './constants';
import { computed, reactive, ref, inject, watch } from 'vue';
import { useFileDialog, useConfirmDialog } from '@vueuse/core';
import { useAuthentication } from '@/composables/use-authentication';
import { useStorage } from '@/composables/use-storage';
import { union, clone, isEmpty } from 'lodash';
import { useAttachments } from './composables';
import 'viewerjs/dist/viewer.css';
import { component as Viewer } from 'v-viewer';

export default {
  props: {
    showLabel: Boolean,
    type: String,
    subtype: String,
    title: String,
  },
  components: {
    Viewer,
  },
  emits: ['showSecondModal', 'success', 'error'],
  setup (props, { emit }) {
    const {
      currentUser,
    } = useAuthentication();
    const { submit: submitAttachments } = useAttachments();
    const {
      upload: uploadFile,
      // locateFile
    } = useStorage();
    const {
      // files,
      open: openDialog,
      // reset: resetDialog,
      onChange,
    } = useFileDialog({
      accept: 'image/*',
    });
    const {
      isRevealed,
      reveal,
      confirm,
      cancel,
    } = useConfirmDialog();

    const openConfirmDialog = async (attachments, index) => {
      const {
        data, // eslint-disable-line
        isCanceled,
      } = await reveal();
      if (!isCanceled) {
        // User confirmed, now you can perform the delete action
        await confirmDeleteAttachment(attachments, index);
      }
    };

    const confirmDeleteAttachment = async (attachments, index) => {
      if (!attachments?.length) return;
      const attachmentList = clone(attachments);
      attachmentList.splice(index, 1);
      model.attachmentURLs = attachmentList;
    };

    const loading = ref(false);
    const record = ref({});
    const recordId = computed(() => record.value.id);
    const isEditing = computed(() => !!recordId.value);

    const mountState = ref(false);
    const savedImage = inject('secondModalData');
    watch(savedImage, async (newVal) => {
      const img = [newVal];
      model.attachmentURLs = union(model.attachmentURLs, img);
    }, { deep: true });

    const model = reactive(FORM_MODEL);
    onChange(async (files) => {
      const rawFilesUrl = await Promise.all(Array.from(files).map(file => {
        return new Promise((resolve, reject) => {
          const reader = new window.FileReader();
          reader.onload = () => {
            resolve(reader.result);
          };
          reader.onerror = (error) => {
            reject(new Error(error));
          };
          reader.readAsDataURL(file);
        });
      }));
      model.attachmentURLs = union(model.attachmentURLs, rawFilesUrl);
    });

    const onChangeFile = (callback) => {
      callback();
    };

    const viewer = ref(null);
    const inited = (viewer) => {
      viewer.value = viewer;
    };

    const submit = async () => {
      try {
        loading.value = true;

        // SAVE FILES
        const existingAttachments = model.attachmentURLs.filter(attachment => attachment?.startsWith('https://'));
        const rawAttachments = model.attachmentURLs.filter(attachment => !attachment?.startsWith('https://'));
        const filesUrl = await Promise.all(rawAttachments.map(async (url) => {
          const link = await uploadFile({ dataURI: url, owner: currentUser.id });
          return link.url;
        }));
        model.attachmentURLs = union(existingAttachments, filesUrl);

        const payload = Object.fromEntries(
          Object.entries(model).filter(([key, value]) => Boolean(value)),
        );
        if (isEditing.value) {
          payload.id = recordId.value;
        }
        // Create
        await submitAttachments(payload);
        resetForm();
        emit('submit');
        emit('success');
      } catch (e) {
        console.error('Something went wrong. Try again later.');
        emit('error', e);
      } finally {
        loading.value = false;
      }
    };

    const openDrawDialog = () => {
      mountState.value = !mountState.value;
      emit('showSecondModal', {
        title: 'Draw Image',
        // subtitle: 'Upload an image of at most 1000x1000 for best quality',
        autoMount: mountState.value,
      });
    };
    const formSubmittable = computed(() => {
      for (const [key, val] of Object.entries(model)) {
        if (FORM_RULES[key].required && (val === '' || val === null || val === undefined || isEmpty(val))) return false;
        continue;
      }
      return true;
    });

    function resetForm () {
      record.value = {};
      UPDATE_FIELDS.forEach(field => {
        model[field] = '';
      });
    }

    // Sets value to record for editing purposes
    // as well as set value model.text
    function setForm (data) {
      if (!data) return;
      record.value = data;
      model.attachmentURLs = data.attachmentURLs;
      model.title = data.title;
      model.description = data.description;
    }

    return {
      loading,
      model,
      recordId,
      isEditing,
      formFields: FORM_FIELDS,
      formSubmittable,
      resetForm,
      submit,
      setForm,

      openDialog,
      openConfirmDialog,
      openDrawDialog,
      confirmDeleteAttachment,
      onChangeFile,

      viewer,
      inited,

      isRevealed,
      reveal,
      confirm,
      cancel,
    };
  },
};
</script>

<style scoped>
select:required:invalid {
  color: gray;
}
option[value=""][disabled] {
  display: none;
}
option {
  color: black;
}
</style>
